import { createSlice, createSelector, createAction } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of } from "rxjs";
import { switchMap, pluck, map, catchError } from "rxjs/operators";
import { CURRENCY_FEATURE_KEY } from "@nxplatform/store-shared/geo-data/shared-geo-data-feature-keys";
import { getCurrencyState, selectExchangeRates } from "@nxplatform/store-shared/geo-data/shared-geo-data-selectors";
import { AppStorage } from "@service/api-client/http/helpers/AppStorage";
const currencySliceKey = "AppstoragetCurrency";
const initialCurrencyState = {
  currencies: AppStorage.get(currencySliceKey) || [],
  cryptoAlternative: [],
  exchangeRates: [],
  cryptoAlternativesGame: [],
  loaded: !!AppStorage.get(currencySliceKey),
  error: null
};
const currencySlice = createSlice({
  name: CURRENCY_FEATURE_KEY,
  initialState: initialCurrencyState,
  reducers: {
    getCurrencyStart: (state, action) => {
    },
    getCurrencySuccess: (state, action) => {
      state.loaded = true;
      state.currencies = action.payload;
    },
    getCurrencyFailure: (state, action) => {
      state.error = action.payload;
    },
    getCryptoAlternativeStart: (state, action) => {
    },
    getCryptoAlternativeSuccess: (state, action) => {
      state.cryptoAlternative = action.payload;
    },
    getCryptoAlternativeFailure: (state, action) => {
    },
    getExchangeRatesStart: (state, action) => {
    },
    getExchangeRatesSuccess: (state, action) => {
      state.exchangeRates = action.payload;
    },
    getExchangeRatesFailure: (state, action) => {
      state.error = action.payload;
    },
    getCryptoAlternativesGameSuccess: (state, action) => {
      state.cryptoAlternativesGame = action.payload;
    },
    getCryptoAlternativesGameFailure: (state, action) => {
      state.error = action.payload;
    }
  }
});
const currencyReducer = currencySlice.reducer;
const {
  getCurrencyStart,
  getCurrencySuccess,
  getCurrencyFailure,
  getCryptoAlternativeStart,
  getCryptoAlternativeSuccess,
  getCryptoAlternativeFailure,
  getExchangeRatesStart,
  getExchangeRatesFailure,
  getExchangeRatesSuccess,
  getCryptoAlternativesGameSuccess,
  getCryptoAlternativesGameFailure
} = currencySlice.actions;
const selectCurrencyEntities = createSelector(
  getCurrencyState,
  (s) => s.currencies
);
const selectCryptoCurrencies = createSelector(
  selectCurrencyEntities,
  (currencies) => currencies.filter((currency) => !!currency.isCrypto)
);
const selectFiatCurrencies = createSelector(
  selectCurrencyEntities,
  (currencies) => currencies.filter((currency) => !currency.isCrypto)
);
const selectCurrencyEntitiesToAppSelect = createSelector(
  getCurrencyState,
  (s) => s.currencies.map((currency) => ({
    id: currency.code,
    name: currency.code,
    text: currency.description
  }))
);
const selectCurrencyCustomerEntitiesToAppSelect = createSelector(
  getCurrencyState,
  (s, customerCurrencies) => customerCurrencies || [],
  (s, customerCurrencies) => customerCurrencies.map((currency) => ({
    id: currency.currency,
    name: currency.currency_name,
    text: currency.currency_name
  }))
);
const selectBaseCurrencyByCode = createSelector(
  getCurrencyState,
  (s, code) => code,
  (s, code) => {
    var _a;
    return (_a = s == null ? void 0 : s.currencies) == null ? void 0 : _a.find((currency) => currency.code === code);
  }
);
const selectCryptoAlternativeCurrencyByCode = createSelector(
  getCurrencyState,
  (s, code) => code,
  (s, code) => {
    var _a;
    return (_a = s == null ? void 0 : s.cryptoAlternative) == null ? void 0 : _a.find((currency) => currency.code === code);
  }
);
const selectCurrencyByCode = createSelector(
  [selectBaseCurrencyByCode, selectCryptoAlternativeCurrencyByCode],
  (currency, cryptoAlternative) => currency || cryptoAlternative
);
const selectCurrenciesToDropDown = createSelector(
  selectCurrencyEntities,
  (entities) => entities.map((entity) => ({ id: entity.code, text: entity.name }))
);
const selectCurrencyByName = createSelector(
  (currencies) => currencies,
  (currencies, name) => name,
  (currencies, name) => currencies.find((currency) => currency.text === name)
);
const selectCurrencyLoaded = createSelector(
  getCurrencyState,
  (s) => s.loaded
);
const selectCurrencyError = createSelector(
  getCurrencyState,
  (s) => s.error
);
const selectCryptoAlternative = createSelector(
  getCurrencyState,
  (s) => s.cryptoAlternative
);
const selectCryptoAlternativesGame = createSelector(
  getCurrencyState,
  (s) => s.cryptoAlternativesGame
);
const selectReverseAlternativeCurrencyRateToSetCurrency = createSelector(
  [
    selectExchangeRates,
    (s, baseCurrency) => baseCurrency,
    (s, baseCurrency, setCurrency) => setCurrency
  ],
  (rates, baseCurrency, setCurrency) => {
    const reverseRate = rates.find(
      (rate) => rate.codeFrom === setCurrency && rate.codeTo === baseCurrency
    );
    if (reverseRate) {
      return {
        codeFrom: reverseRate.codeTo,
        codeTo: reverseRate.codeFrom,
        value: 1 / reverseRate.value
      };
    }
  }
);
const selectCryptoAlternativeRate = createSelector(
  getCurrencyState,
  (s, crypto) => crypto,
  (s, crypto, alternate) => alternate,
  (s, crypto, alternate) => {
    if (!crypto || !alternate) {
      return 1;
    }
    const alternateItem = (s.cryptoAlternative || []).find((item) => item.code === alternate);
    const { enabledCryptoRates } = alternateItem || {};
    if (enabledCryptoRates) {
      const rate = enabledCryptoRates.find((item) => item.codeFrom === crypto);
      return rate ? rate.value : 1;
    }
    return 1;
  }
);
const currencyEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getCurrencyStart.type),
  switchMap((action) => Api.geoClient.getCurrencyList(action.payload).pipe(
    pluck("currencies"),
    map((currencies) => getCurrencySuccess(
      currencies.filter((currency) => !!Number(currency.enabled))
    ))
  ))
);
const fetchCurrencies = (queryParams) => ({ type: getCurrencyStart.type, payload: queryParams || "" });
const cryptoAlternativeEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getCryptoAlternativeStart.type),
  switchMap(() => Api.customerNoAuth.getCryptoAlternative().pipe(
    pluck("items"),
    map((data) => getCryptoAlternativeSuccess(data))
  ))
);
const fetchCryptoAlternative = () => ({ type: getCryptoAlternativeStart.type });
const exchangeRatesEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getExchangeRatesStart.type),
  switchMap(() => Api.customerNoAuth.getExchangeRates().pipe(
    pluck("items"),
    map((data) => getExchangeRatesSuccess(data)),
    catchError((err) => of(getExchangeRatesFailure(err)))
  ))
);
const fetchExchangeRates = () => ({ type: getExchangeRatesStart.type });
const fetchCryptoAlternativeCasinoGame = createAction(
  `${CURRENCY_FEATURE_KEY}/fetchCryptoAlternativeCasinoGame`
);
const cryptoAlternativeCasinoGameEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(fetchCryptoAlternativeCasinoGame.type),
  switchMap((action) => Api.customerNoAuth.getCryptoAlternativeGame(action.payload).pipe(
    pluck("items"),
    map((data) => getCryptoAlternativesGameSuccess(data)),
    catchError((err) => of(getCryptoAlternativesGameFailure(err)))
  ))
);
export {
  cryptoAlternativeCasinoGameEpic,
  cryptoAlternativeEpic,
  currencyEpic,
  currencyReducer,
  currencySlice,
  currencySliceKey,
  exchangeRatesEpic,
  fetchCryptoAlternative,
  fetchCryptoAlternativeCasinoGame,
  fetchCurrencies,
  fetchExchangeRates,
  getCryptoAlternativeFailure,
  getCryptoAlternativeStart,
  getCryptoAlternativeSuccess,
  getCryptoAlternativesGameFailure,
  getCryptoAlternativesGameSuccess,
  getCurrencyFailure,
  getCurrencyStart,
  getCurrencySuccess,
  getExchangeRatesFailure,
  getExchangeRatesStart,
  getExchangeRatesSuccess,
  initialCurrencyState,
  selectBaseCurrencyByCode,
  selectCryptoAlternative,
  selectCryptoAlternativeCurrencyByCode,
  selectCryptoAlternativeRate,
  selectCryptoAlternativesGame,
  selectCryptoCurrencies,
  selectCurrenciesToDropDown,
  selectCurrencyByCode,
  selectCurrencyByName,
  selectCurrencyCustomerEntitiesToAppSelect,
  selectCurrencyEntities,
  selectCurrencyEntitiesToAppSelect,
  selectCurrencyError,
  selectCurrencyLoaded,
  selectFiatCurrencies,
  selectReverseAlternativeCurrencyRateToSetCurrency
};
